import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ventOptions } from '../data';

// Friendly labels for the raw material keys stored in data.js.
const MATERIAL_LABELS = {
  steel_24_gauge: 'Carbon steel (24 ga)',
  stainless_430: '430 stainless',
  stainless_316: '316 stainless',
  satin_coat_steel: 'Satin-coat steel',
  air: 'Air gap',
  ceramic_fiber: 'Ceramic fiber',
  duraplus_middle_combined: 'Combined middle (effective)',
  brick_clay: 'Clay brick',
  stainless_plus_vermiculite: '316 liner + vermiculite (effective)',
  stainless_plus_mineral_wool: '316 liner + mineral wool (effective)',
};
const materialLabel = (key) => MATERIAL_LABELS[key] || (key ? key.replace(/_/g, ' ') : '—');

const CATEGORY_LABELS = {
  stove_pipe: 'Stove pipe',
  chimney: 'Chimney',
  masonry: 'Lined masonry chimney',
};
const CATEGORY_ORDER = ['stove_pipe', 'chimney', 'masonry'];

// meters -> mm (trimmed); null-safe.
const mm = (m) => (m == null ? null : Number((m * 1000).toFixed(3)));
const fmt = (v) => (v == null ? '—' : v);

// Series colors (Okabe-Ito), matching the Results charts.
const FIRE_COLORS = { Low: '#0072B2', Medium: '#E69F00', High: '#D55E00' };

// Build the inner/middle/outer rows present on a vent option. A single-layer
// option (single-wall pipe) is labeled "Wall".
function layerRows(opt) {
  const layers = [
    ['Inner', opt.inner],
    ['Middle', opt.middle],
    ['Outer', opt.outer],
  ].filter(([, l]) => l);
  if (layers.length === 1) layers[0][0] = 'Wall';
  return layers.map(([label, l]) => ({
    label,
    material: materialLabel(l.material),
    thickness: mm(l.thickness_m),
    density: l.density_kg_m3,
    k: l.k_W_m_K,
    cp: l.cp_J_kg_K,
    eps: l.emissivity,
    rough: mm(l.roughness_m),
  }));
}

function MaterialTable({ opt }) {
  const rows = layerRows(opt);
  return (
    <div className="assumptions-material">
      <h4>{opt.name}{opt.max_flue_temp_C ? ` (max flue temp ${opt.max_flue_temp_C} °C)` : ''}</h4>
      {opt.description && <p className="assumptions-desc">{opt.description}</p>}
      <table className="user-guide-table">
        <thead>
          <tr>
            <th>Layer</th><th>Material</th><th>Thickness (mm)</th>
            <th>ρ (kg/m³)</th><th>k (W/m·K)</th><th>cp (J/kg·K)</th>
            <th>ε</th><th>Roughness (mm)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td><td>{r.material}</td><td>{fmt(r.thickness)}</td>
              <td>{fmt(r.density)}</td><td>{fmt(r.k)}</td><td>{fmt(r.cp)}</td>
              <td>{fmt(r.eps)}</td><td>{fmt(r.rough)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// seconds -> m:ss
const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Kelvin → display unit ('C' or 'F'), rounded.
const kToDisplay = (K, unit) =>
  unit === 'F' ? Math.round((K - 273.15) * 9 / 5 + 32) : Math.round(K - 273.15);

// Celsius → display unit ('C' or 'F'), rounded (used for the hard-coded
// peak/settles values shown in the table).
const cToDisplay = (C, unit) =>
  unit === 'F' ? Math.round(C * 9 / 5 + 32) : C;

// Merge the three profiles onto a shared time axis. Each profile only carries a
// value at its OWN sample times; the rest are null so connectNulls draws the
// piecewise-linear segments between a profile's real points (the engine
// interpolates the same way).
function buildFireRows(fireProfiles, tempUnit) {
  const series = Object.values(fireProfiles);
  const times = [...new Set(series.flatMap((p) => p.inletTempProfile.map(([t]) => t)))]
    .sort((a, b) => a - b);
  return times.map((t) => {
    const row = { time: Number((t / 60).toFixed(2)) };
    series.forEach((p) => {
      const pt = p.inletTempProfile.find(([tt]) => tt === t);
      row[p.label] = pt ? kToDisplay(pt[1], tempUnit) : null;
    });
    return row;
  });
}

const Assumptions = ({ fireProfiles = {}, doorSchedule = [] }) => {
  const [tempUnit, setTempUnit] = useState('C');
  const fireRows = buildFireRows(fireProfiles, tempUnit);
  const fireLabels = Object.values(fireProfiles).map((p) => p.label);
  const unitLabel = tempUnit === 'F' ? '°F' : '°C';

  return (
    <div className="user-guide assumptions">
      <h2>Assumptions</h2>
      <p>
        The tool runs a transient heat-transfer and draft simulation. The values
        below are the fixed defaults and modeling assumptions it uses unless you
        change an input.
      </p>

      <h3>1. Material properties</h3>
      <p>
        Each vent type is modeled as a single layer (single-wall pipe) or three
        layers (inner / middle / outer) for double-wall, Class&nbsp;A, and masonry.
      </p>
      {CATEGORY_ORDER.map((cat) => {
        const opts = ventOptions.filter((v) => v.category === cat);
        if (opts.length === 0) return null;
        return (
          <div key={cat}>
            <h4 className="assumptions-cat">{CATEGORY_LABELS[cat]}</h4>
            {opts.map((opt) => <MaterialTable key={opt.id} opt={opt} />)}
          </div>
        );
      })}
      <p className="assumptions-note">
        Effective-property note: the lumped masonry inner layers and the
        Triple-wall combined middle are simplifications of multi-layer stacks.
        Their effective k preserves the total series thermal resistance and their
        effective ρ·cp preserves the total thermal mass of the real assembly.
        Masonry rectangular flues are treated as a round-equivalent (hydraulic)
        diameter, and the brick exterior is the heat-loss boundary (masonry parts
        default to the outdoor ambient temperature).
      </p>

      <h3>2. Door schedule</h3>
      <p>
        Every fire profile uses the same door-opening schedule (door opening as a
        percentage of fully open, vs. time from light-up; interpolated linearly):
      </p>
      <table className="user-guide-table">
        <thead><tr><th>Time (minutes:seconds)</th><th>Door opening</th></tr></thead>
        <tbody>
          {doorSchedule.map(([t, pct]) => (
            <tr key={t}><td>{fmtTime(t)}</td><td>{pct}%</td></tr>
          ))}
        </tbody>
      </table>
      <p className="assumptions-note">
        The door is held wide open for the first ~1.5 minutes, cracked to a ~2″
        opening and held, then closed at 3:00.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>3. Fire profiles (preset inlet-gas temperature curves)</h3>
        <button
          type="button"
          onClick={() => setTempUnit((u) => (u === 'C' ? 'F' : 'C'))}
          style={{
            padding: '2px 10px',
            fontSize: '13px',
            border: '1px solid var(--border-mid)',
            borderRadius: '999px',
            background: 'var(--surface-readout-raised)',
            color: 'var(--text-on-dark-secondary)',
            cursor: 'pointer',
          }}
          aria-label="Toggle temperature unit"
          title="Toggle °C / °F"
        >
          <span style={{ fontWeight: tempUnit === 'C' ? 700 : 400, opacity: tempUnit === 'C' ? 1 : 0.55 }}>°C</span>
          <span style={{ margin: '0 6px', opacity: 0.5 }}>|</span>
          <span style={{ fontWeight: tempUnit === 'F' ? 700 : 400, opacity: tempUnit === 'F' ? 1 : 0.55 }}>°F</span>
        </button>
      </div>
      <p>
        The Fire Profile selector sets the flue-gas inlet temperature over time.
        All three start at ambient ({cToDisplay(20, tempUnit)} {unitLabel}), spike during kindling, then decay to a
        steady tail. They share the door schedule above; only the temperature
        curve differs.
      </p>
      <table className="user-guide-table">
        <thead><tr><th>Profile</th><th>Peak</th><th>Time to peak</th><th>Settles to</th></tr></thead>
        <tbody>
          <tr><td>Low</td><td>~{cToDisplay(400, tempUnit)} {unitLabel}</td><td>~4.4 min</td><td>~{cToDisplay(280, tempUnit)} {unitLabel}</td></tr>
          <tr><td>Medium</td><td>~{cToDisplay(600, tempUnit)} {unitLabel}</td><td>~3.9 min</td><td>~{cToDisplay(420, tempUnit)} {unitLabel}</td></tr>
          <tr><td>High</td><td>~{cToDisplay(700, tempUnit)} {unitLabel}</td><td>~3.4 min</td><td>~{cToDisplay(450, tempUnit)} {unitLabel}</td></tr>
        </tbody>
      </table>
      <div className="assumptions-chart">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={fireRows} margin={{ top: 10, right: 20, left: 10, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-mid)" />
            <XAxis
              dataKey="time" type="number" domain={[0, 30]}
              ticks={[0, 5, 10, 15, 20, 25, 30]}
              label={{ value: 'Time (min)', position: 'insideBottom', offset: -4, fill: 'var(--text-on-dark-muted)', fontSize: 13 }}
              tick={{ fill: 'var(--text-on-dark-muted)', fontSize: 12 }}
            />
            <YAxis
              label={{ value: `Inlet gas temp (${unitLabel})`, angle: -90, position: 'insideLeft', dy: 60, fill: 'var(--text-on-dark-muted)', fontSize: 13 }}
              tick={{ fill: 'var(--text-on-dark-muted)', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{ background: 'var(--surface-readout-raised)', border: '1px solid var(--border-dark)', color: 'var(--text-on-dark-secondary)' }}
              labelFormatter={(v) => `${v} min`}
              formatter={(v, n) => [`${v} ${unitLabel}`, n]}
            />
            <Legend wrapperStyle={{ fontSize: 13 }} />
            {fireLabels.map((label) => (
              <Line
                key={label} type="monotone" dataKey={label} name={label}
                stroke={FIRE_COLORS[label] || 'var(--text-dimmer)'} dot={false} strokeWidth={2}
                connectNulls isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <h3>4. Simulation settings &amp; other assumptions</h3>
      <p><strong>Run settings</strong></p>
      <ul>
        <li>Time step: 0.5 s.</li>
        <li>Simulated duration: 600 s (10 minutes).</li>
        <li>Site elevation (your input) adjusts the ambient air pressure used.</li>
      </ul>
      <p><strong>Air inlet / door restriction</strong></p>
      <ul>
        <li>Swivel door, 15.25″ wide × 9.875″ tall.</li>
        <li>Discharge coefficient C<sub>d</sub> = 0.62.</li>
        <li>Calibrated air-leak area = 0.0075 m².</li>
        <li>Minimum inlet area = 0.003 m².</li>
        <li>Stove flow-loss coefficient K<sub>stove</sub> = 0.</li>
      </ul>
      <p><strong>Geometry &amp; layout</strong></p>
      <ul>
        <li>Diameter is selectable from 3″ to 8″; default 6″.</li>
        <li>Parts are auto-tagged indoor or outdoor by position relative to the wall/roof, which sets the default Surrounding Temp (overridable per part).</li>
        <li>Default ambient temperatures: <strong>indoor ≈ 65 °F (18 °C)</strong>, <strong>outdoor ≈ 35 °F (1.7 °C)</strong>. A part-level Surrounding Temp entry overrides the default for that part.</li>
        <li>Lined masonry parts always use the outdoor default at the brick (outer) boundary; the brick face is the heat-loss surface.</li>
      </ul>
      <p><strong>Room depressurization</strong></p>
      <ul>
        <li>The Room Depressurization input (Pa) shifts the stove-room reference pressure used by the draft balance.</li>
        <li><strong>Viability cutoff: 2.5 Pa.</strong> Above this, the design is reported as <em>not viable</em> on the Results page (no charts shown). The threshold is a usability gate, not a model limit.</li>
      </ul>
      <p><strong>Fittings (defaults)</strong></p>
      <ul>
        <li>90° elbow: long-radius, bend radius / diameter R/D = 1.5.</li>
        <li>Tee: vertical leg 1.0 ft, horizontal leg ~0.54 ft.</li>
        <li>Multiple bends at one junction are combined (their angles add).</li>
      </ul>
      <p><strong>Wall-type → model</strong></p>
      <ul>
        <li>Single-wall pipe → single-layer thermal model.</li>
        <li>Double-wall, Class A, and masonry → three-layer thermal model.</li>
        <li>Air gaps are accounted for within the middle layer.</li>
      </ul>
    </div>
  );
};

export default Assumptions;
